import React from 'react'
import styled from 'styled-components'
import { Flex, Text, TextVariant } from '@mprolab/uikit'
import { ArrowRight } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import MainWrapper from 'layouts/MainWrapper'
import { StyledBiennale } from 'components'
import { useGetNewsQuery } from 'state/services/community/community'

/* See the Community view: MainWrapper is a row Flex with no vertical offset. */
const Column = styled(Flex)`
  width: 100%;
  flex-direction: column;
  margin: 152px 0 120px;
`

/**
 * One third and two thirds, stacked on a narrow screen.
 *
 * The introduction is the narrower side deliberately: it is read once, and the
 * news is what somebody comes back for.
 */
const Columns = styled(Flex)`
  width: 100%;
  gap: 40px;
  align-items: flex-start;
  flex-direction: column;

  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
    gap: 72px;
  }
`

const Intro = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.md} {
    width: 33.333%;
    flex-shrink: 0;
  }
`

const NewsColumn = styled(Flex)`
  flex-direction: column;
  gap: 16px;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.md} {
    flex: 1;
    min-width: 0;
  }
`

const Title = styled(StyledBiennale)`
  font-size: 40px;
  line-height: 1.1;
`

/** The same face as the title on the left, so the two columns are siblings. */
const NewsHeading = styled(StyledBiennale)`
  font-size: 40px;
  line-height: 1.1;
  margin-bottom: 8px;
`

const Claim = styled(Text)`
  color: ${({ theme }) => theme.colors.cyan};
  letter-spacing: 0.04em;
  text-transform: lowercase;
`

const Body = styled(Text)`
  color: ${({ theme }) => theme.colors.gray2};
  line-height: 1.65;
`

/** The border the platform's tables use, so the feed reads as part of it. */
const Row = styled.article`
  width: 100%;
  padding: 20px 22px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.background};
  transition: border-color 150ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.gray6};
  }
`

const RowTitle = styled(Text)`
  display: block;
  margin-bottom: 6px;
`

const Meta = styled(Text)`
  opacity: 0.6;
`

const Excerpt = styled(Text)`
  display: block;
  margin: 10px 0 14px;
  color: ${({ theme }) => theme.colors.gray2};
  line-height: 1.6;
`

const ReadMore = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.cyan};
`

const INTRO = [
  'Welcome to the MPRO Lab community portal. The portal exists so the community itself governs the distribution of the MPRO token and the iNODE licences.',
  'To join us you need to hold an iNODE licence NFT. You can post a buy offer on the marketplace, or receive one from an existing member.',
  'The number of licences is fixed and closed. A licence entitles you to a share of the MPRO distribution, a vote in the DAO, active participation in the community, the use of platform features such as Storage, and a share of the distributed platform transaction fee.',
  'More features for MPRO Lab community members are coming soon.',
  'The platform is maintained and moderated by members of the community. No organisation or company stands behind it. Together we are working towards a fully autonomous DAO.'
]

/**
 * The opening of a note, as plain text.
 *
 * Markdown is stripped rather than rendered: a teaser that renders a heading
 * or a list fights the row it sits in, and a half-rendered table is worse than
 * a sentence. The full note is one click away and renders properly there.
 */
function excerpt(markdown: string, limit = 220): string {
  const plain = markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^[#>\-*\s]+/gm, ' ')
    .replace(/[*_`]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  if (plain.length <= limit) return plain
  const cut = plain.slice(0, limit)
  // Break on a word, not mid-syllable.
  return `${cut.slice(0, cut.lastIndexOf(' ')).trimEnd()}…`
}

const Home: React.FC = () => {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useGetNewsQuery({ limit: 10 })
  const news = data?.news ?? []

  return (
    <MainWrapper>
      <Column>
        <Columns>
          <Intro>
            <Title>MPRO Lab</Title>
            <Claim variant={TextVariant.BODY_DEFAULT_MEDIUM}>community portal</Claim>
            {INTRO.map((paragraph) => (
              <Body key={paragraph.slice(0, 24)} variant={TextVariant.BODY_SMALL}>
                {paragraph}
              </Body>
            ))}
          </Intro>

          <NewsColumn>
            <NewsHeading>Community News</NewsHeading>
            {isLoading && <Meta variant={TextVariant.BODY_SMALL}>Loading…</Meta>}
            {/*
              Three states, not two: a feed that failed to load must not be
              rendered as a community with nothing to say.
            */}
            {!isLoading && isError && (
              <Meta variant={TextVariant.BODY_SMALL}>
                Could not load the news just now.
              </Meta>
            )}
            {!isLoading && !isError && news.length === 0 && (
              <Meta variant={TextVariant.BODY_SMALL}>No news yet.</Meta>
            )}
            {news.map((item) => (
              <Row key={item.id}>
                <RowTitle variant={TextVariant.BODY_BIG_MEDIUM}>{item.title}</RowTitle>
                <Meta variant={TextVariant.TEXT_XS_REGULAR}>
                  {item.publishedAt ? new Date(item.publishedAt).toLocaleDateString() : ''}
                  {item.byline ? ` · ${item.byline}` : ''}
                </Meta>
                <Excerpt variant={TextVariant.BODY_SMALL}>{excerpt(item.body)}</Excerpt>
                <ReadMore onClick={() => navigate(`/news/${item.id}`)}>
                  Read more <ArrowRight size={14} />
                </ReadMore>
              </Row>
            ))}
          </NewsColumn>
        </Columns>
      </Column>
    </MainWrapper>
  )
}

export default Home
